import { useEffect, useRef, useState } from "react";

/**
 * Undo affordance for an unfollow (T212649).
 *
 * UNDESIGNED — nothing exists for this in any attachment (design spec §1.5), so it is built
 * from the site's existing toast language and flagged for design review.
 *
 * The unfollow has ALREADY happened server-side by the time this appears; the row is
 * removed optimistically because that is what the ticket asks for. "Undo" therefore issues
 * a re-follow rather than cancelling anything in flight, which is why it can still be
 * honest about the outcome if the re-follow fails.
 *
 * `role="status"` rather than `role="alert"`: this is a confirmation, not an error, and
 * should not interrupt what the user is doing.
 */
const UNDO_WINDOW_MS = 5000;

export const UndoBar = ({ pending, copy, onUndo, onExpire }) => {
  const [remaining, setRemaining] = useState(UNDO_WINDOW_MS);
  const expireRef = useRef(onExpire);
  expireRef.current = onExpire;

  useEffect(() => {
    if (!pending) {
      return undefined;
    }

    setRemaining(UNDO_WINDOW_MS);

    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      setRemaining(Math.max(0, UNDO_WINDOW_MS - (Date.now() - startedAt)));
    }, 100);
    const expiry = window.setTimeout(() => expireRef.current?.(pending), UNDO_WINDOW_MS);

    return () => {
      window.clearInterval(tick);
      window.clearTimeout(expiry);
    };
    // Keyed on the pending entry so a second unfollow restarts the window cleanly.
  }, [pending]);

  if (!pending) {
    return null;
  }

  const name = pending.influencer.username
    ? `@${pending.influencer.username}`
    : pending.influencer.displayName;

  return (
    <div className="influencer-discovery__undo" role="status" data-testid="influencer-undo-bar">
      <span className="influencer-discovery__undo-message">
        {copy.undoUnfollowMessage.replace("{0}", name)}
      </span>

      <button
        type="button"
        className="influencer-discovery__undo-button"
        data-testid="influencer-undo-button"
        onClick={() => onUndo(pending)}
      >
        {copy.undoLabel}
      </button>

      <span
        className="influencer-discovery__undo-progress"
        style={{ "--undo-remaining": `${(remaining / UNDO_WINDOW_MS) * 100}%` }}
        aria-hidden="true"
      />
    </div>
  );
};

export { UNDO_WINDOW_MS };
